import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type TourId =
  | 'welcome'
  | 'dashboard'
  | 'pipeline-new'
  | 'pipeline-queue'
  | 'pipeline-approved'
  | 'pipeline-uploaded'
  | 'pipeline-returned'
  | 'pipeline-issues'
  | 'case-review'
  | 'settings'
  | 'sidebar';

interface OnboardingState {
  // UI state
  isTOCOpen: boolean;
  activeTour: TourId | null;

  // Persistence
  completedTours: TourId[];
  hasSeenWelcomePrompt: boolean;
  welcomePromptDismissed: boolean; // "Don't ask again"
  hasSeenReviewPrompt: boolean;
  reviewPromptDismissed: boolean; // "Don't ask again" for review tour

  // Actions
  openTOC: () => void;
  closeTOC: () => void;
  startTour: (tourId: TourId) => void;
  endTour: () => void;
  completeTour: (tourId: TourId) => void;
  markWelcomePromptSeen: () => void;
  dismissWelcomePrompt: () => void;
  markReviewPromptSeen: () => void;
  dismissReviewPrompt: () => void;
  resetProgress: () => void;
  isTourCompleted: (tourId: TourId) => boolean;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      // Initial state
      isTOCOpen: false,
      activeTour: null,
      completedTours: [],
      hasSeenWelcomePrompt: false,
      welcomePromptDismissed: false,
      hasSeenReviewPrompt: false,
      reviewPromptDismissed: false,

      // Actions
      openTOC: () => set({ isTOCOpen: true }),
      closeTOC: () => set({ isTOCOpen: false }),

      startTour: (tourId) => set({ activeTour: tourId, isTOCOpen: false }),
      endTour: () => set({ activeTour: null }),

      completeTour: (tourId) => {
        const { completedTours } = get();
        if (!completedTours.includes(tourId)) {
          set({ completedTours: [...completedTours, tourId] });
        }
        set({ activeTour: null });
      },

      markWelcomePromptSeen: () => set({ hasSeenWelcomePrompt: true }),
      dismissWelcomePrompt: () => set({
        hasSeenWelcomePrompt: true,
        welcomePromptDismissed: true
      }),

      markReviewPromptSeen: () => set({ hasSeenReviewPrompt: true }),
      dismissReviewPrompt: () => set({
        hasSeenReviewPrompt: true,
        reviewPromptDismissed: true
      }),

      resetProgress: () => set({
        completedTours: [],
        hasSeenWelcomePrompt: false,
        welcomePromptDismissed: false,
        hasSeenReviewPrompt: false,
        reviewPromptDismissed: false,
      }),

      isTourCompleted: (tourId) => get().completedTours.includes(tourId),
    }),
    {
      name: 'onboarding-storage',
      partialize: (state) => ({
        completedTours: state.completedTours,
        hasSeenWelcomePrompt: state.hasSeenWelcomePrompt,
        welcomePromptDismissed: state.welcomePromptDismissed,
        hasSeenReviewPrompt: state.hasSeenReviewPrompt,
        reviewPromptDismissed: state.reviewPromptDismissed,
      }),
    }
  )
);
